/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { BrandColors, MaterialIcon, Text } from '@blend/enterprise-components';
import { uniq } from 'lodash';
import { BlendBridge } from 'blb-types/blendBridge';
import { FailedTabExtractionAuditEvent, FailedTabExtractionAuditEventMeta } from 'blb-ui-types/BridgeTypes';

export const styles = StyleSheet.create({
  warningWrapper: {
    display: 'flex',
    marginBottom: '1rem',
    alignItems: 'center',
    gap: '0.5rem',
  },
});

export namespace DocWarnings {
  export interface Props {
    loan: BlendBridge.API.Loan;
  }
}

export const DocWarnings: React.FC<DocWarnings.Props> = ({ loan }) => {
  const { closing } = loan;
  if (closing === undefined) {
    return null;
  }

  if (!['HYBRID', 'RON'].includes(closing.closingType)) {
    return null;
  }

  const hasMetaDocumentFields = (value: any): value is FailedTabExtractionAuditEventMeta => {
    const { docLosType, documentID, originalClosingDesignation } = value;
    return (
      typeof docLosType === 'string' && typeof documentID === 'string' && typeof originalClosingDesignation === 'string'
    );
  };

  const filterEvents = (event: EClose.AuditEventResponse): event is FailedTabExtractionAuditEvent => {
    const { meta } = event;
    if (meta && hasMetaDocumentFields(meta)) {
      return (
        event.type === 'FAILED_TAB_EXTRACTION' &&
        meta.originalClosingDesignation === 'SIGN_AHEAD' &&
        closing.documentReferences.some(docRef => docRef.documentID === meta.documentID) &&
        // meta.action = undefined is for backward compatible. Before switching to name-based matching, meta.action is undefined.
        (meta.action === undefined || meta.action === 'MOVE_TO_DAY_OF_DESIGNATION')
      );
    } else {
      return false;
    }
  };

  const failedTabExtractionDocs = uniq(closing.auditEvents.filter(filterEvents).map(event => event.meta.docLosType));

  if (failedTabExtractionDocs.length === 0) {
    return null;
  }

  return (
    <div className={css(styles.warningWrapper)}>
      <MaterialIcon color={BrandColors.ACCENT_RED} size={16} type="error" />
      <Text colorName="ACCENT_RED" data-it="doc-warning-message">
        The following documents were moved to the "Ink Sign" package because some signature coordinates could not be
        extracted successfully: {failedTabExtractionDocs.join(', ')}
      </Text>
    </div>
  );
};
